/**
 * Screenshot Annotation API
 *
 * Manages screenshot uploads (Supabase Storage) and annotation pins (KV store)
 * for facelift project pages.
 *
 * Data model:
 *   Each facelift page can have multiple screenshots.
 *   Each screenshot has an array of pin annotations (x%, y%, text).
 *   All screenshot metadata is stored under:
 *     KV key: {templateId}:facelift:screenshots:{pageId}
 */

import { API_BASE, getSessionTokenValue } from "./apiClient";
import { publicAnonKey } from "../../../utils/supabase/info";

/* ================================================================
   Types
   ================================================================ */

export interface AnnotationPin {
  id: string;
  /** X position as percentage (0-100) of image width */
  x: number;
  /** Y position as percentage (0-100) of image height */
  y: number;
  /** Annotation text */
  text: string;
  /** Author name */
  author: string;
  /** Pin color */
  color: string;
  createdAt: number;
}

export interface ScreenshotEntry {
  id: string;
  /** Display label for this screenshot */
  label: string;
  /** Path in Supabase Storage bucket */
  storagePath: string;
  /** Signed URL (may expire \u2014 refresh if needed) */
  signedUrl: string;
  /** When the signed URL was generated */
  signedUrlCreatedAt: number;
  /** Annotation pins */
  annotations: AnnotationPin[];
  createdAt: number;
  updatedAt: number;
}

/* ================================================================
   Helpers
   ================================================================ */

async function apiFetch(path: string, options: RequestInit = {}) {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${publicAnonKey}`,
    ...((options.headers as Record<string, string>) || {}),
  };
  const token = getSessionTokenValue();
  if (token) {
    headers["X-Session-Token"] = token;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });
  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`API error ${res.status}: ${errBody}`);
  }
  return res.json();
}

async function apiFetchJson(path: string, options: RequestInit = {}) {
  return apiFetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
}

const PIN_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e",
  "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6",
];

export function getRandomPinColor(): string {
  return PIN_COLORS[Math.floor(Math.random() * PIN_COLORS.length)];
}

export function generateId(): string {
  return "ss-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 6);
}

/* ================================================================
   Upload
   ================================================================ */

export async function uploadScreenshot(
  templateId: string,
  pageId: string,
  file: File,
): Promise<{ storagePath: string; signedUrl: string }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("templateId", templateId);
  formData.append("pageId", pageId);

  const uploadHeaders: Record<string, string> = {
    Authorization: `Bearer ${publicAnonKey}`,
  };
  const uploadToken = getSessionTokenValue();
  if (uploadToken) {
    uploadHeaders["X-Session-Token"] = uploadToken;
  }
  const res = await fetch(`${API_BASE}/screenshots/upload`, {
    method: "POST",
    headers: uploadHeaders,
    body: formData,
  });

  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`Upload error ${res.status}: ${errBody}`);
  }

  const data = await res.json();
  return { storagePath: data.storagePath, signedUrl: data.signedUrl };
}

/* ================================================================
   Signed URL refresh
   ================================================================ */

export async function refreshSignedUrl(storagePath: string): Promise<string> {
  const data = await apiFetchJson("/screenshots/signed-url", {
    method: "POST",
    body: JSON.stringify({ storagePath }),
  });
  return data.signedUrl || "";
}

/* ================================================================
   Delete screenshot file from storage
   ================================================================ */

export async function deleteScreenshotFile(storagePath: string): Promise<void> {
  await apiFetchJson("/screenshots/file", {
    method: "DELETE",
    body: JSON.stringify({ storagePath }),
  });
}

/* ================================================================
   Get / Save annotations
   ================================================================ */

export async function getScreenshots(
  templateId: string,
  pageId: string,
): Promise<ScreenshotEntry[]> {
  try {
    const data = await apiFetchJson(
      `/screenshots/annotations?templateId=${encodeURIComponent(templateId)}&pageId=${encodeURIComponent(pageId)}`,
    );
    return Array.isArray(data.screenshots) ? data.screenshots : [];
  } catch {
    return [];
  }
}

export async function saveScreenshots(
  templateId: string,
  pageId: string,
  screenshots: ScreenshotEntry[],
): Promise<void> {
  await apiFetchJson("/screenshots/annotations", {
    method: "PUT",
    body: JSON.stringify({ templateId, pageId, screenshots }),
  });
}